import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

// Create the TradingContext
const TradingContext = createContext();

// TradingProvider component to wrap the app
export const TradingProvider = ({
  children,
  initialSelectedIndex = '',
  initialSelectedExpiry = '',
  initialDefaultQuantity = 50,
  initialOptionChainData = null,
  // External state management props (for App.js integration)
  externalSelectedIndex,
  externalSelectedExpiry,
  externalDefaultQuantity,
  externalOptionChainData,
  onIndexChange,
  onExpiryChange,
  onDefaultQuantityChange,
  onOptionChainDataChange
}) => {
  // Internal state (used when no external state is provided)
  const [internalSelectedIndex, setInternalSelectedIndex] = useState(initialSelectedIndex);
  const [internalSelectedExpiry, setInternalSelectedExpiry] = useState(initialSelectedExpiry);
  const [internalDefaultQuantity, setInternalDefaultQuantity] = useState(initialDefaultQuantity);
  const [internalOptionChainData, setInternalOptionChainData] = useState(initialOptionChainData);

  // Use external state if provided, otherwise use internal state
  const selectedIndex = externalSelectedIndex !== undefined ? externalSelectedIndex : internalSelectedIndex;
  const selectedExpiry = externalSelectedExpiry !== undefined ? externalSelectedExpiry : internalSelectedExpiry;
  const defaultQuantity = externalDefaultQuantity !== undefined ? externalDefaultQuantity : internalDefaultQuantity;
  const optionChainData = externalOptionChainData !== undefined ? externalOptionChainData : internalOptionChainData;

  // Determine which setters to use
  const setSelectedIndex = onIndexChange || setInternalSelectedIndex;
  const setSelectedExpiry = onExpiryChange || setInternalSelectedExpiry;

  const setDefaultQuantity = useCallback((qty) => {
    if (onDefaultQuantityChange) {
      onDefaultQuantityChange(qty);
    } else {
      setInternalDefaultQuantity(qty);
    }
  }, [onDefaultQuantityChange]);

  const setOptionChainData = onOptionChainDataChange || setInternalOptionChainData;

  // Sync internal state with external state when external state changes
  useEffect(() => {
    if (externalSelectedIndex !== undefined) {
      setInternalSelectedIndex(externalSelectedIndex);
    }
  }, [externalSelectedIndex]);

  useEffect(() => {
    if (externalSelectedExpiry !== undefined) {
      setInternalSelectedExpiry(externalSelectedExpiry);
    }
  }, [externalSelectedExpiry]);

  useEffect(() => {
    if (externalDefaultQuantity !== undefined) {
      setInternalDefaultQuantity(externalDefaultQuantity);
    }
  }, [externalDefaultQuantity]);

  useEffect(() => {
    if (externalOptionChainData !== undefined) {
      setInternalOptionChainData(externalOptionChainData);
    }
  }, [externalOptionChainData]);

  // Memoized handlers to prevent unnecessary re-renders
  const handleIndexChange = useCallback((index) => {
    if (onIndexChange) {
      onIndexChange(index);
    } else {
      setInternalSelectedIndex(index);
    }
  }, [onIndexChange]);

  const handleExpiryChange = useCallback((expiry) => {
    if (onExpiryChange) {
      onExpiryChange(expiry);
    } else {
      setInternalSelectedExpiry(expiry);
    }
  }, [onExpiryChange]);

  const handleDefaultQuantityChange = useCallback((quantity) => {
    if (onDefaultQuantityChange) {
      onDefaultQuantityChange(quantity);
    } else {
      setInternalDefaultQuantity(quantity);
    }
  }, [onDefaultQuantityChange]);

  const handleOptionChainDataChange = useCallback((data) => {
    if (onOptionChainDataChange) {
      onOptionChainDataChange(data);
    } else {
      setInternalOptionChainData(data);
    }
  }, [onOptionChainDataChange]);

  // Context value with state and handlers
  const value = {
    // State
    selectedIndex,
    selectedExpiry,
    defaultQuantity,
    optionChainData,

    // State setters
    setSelectedIndex,
    setSelectedExpiry,
    setDefaultQuantity,
    setOptionChainData,

    // Handlers (for consistency with existing component interfaces)
    handleIndexChange,
    handleExpiryChange,
    handleDefaultQuantityChange,
    handleOptionChainDataChange,
  };

  return (
    <TradingContext.Provider value={value}>
      {children}
    </TradingContext.Provider>
  );
};

// Custom hook to use the TradingContext with validation
export const useTradingState = () => {
  const context = useContext(TradingContext);

  if (context === undefined) {
    throw new Error('useTradingState must be used within a TradingProvider');
  }

  return context;
};

// Hook for components that need fallback to props (backward compatibility)
export const useTradingStateWithFallback = (propValues = {}) => {
  const context = useContext(TradingContext);

  // If context is not available, use props as fallback
  if (context === undefined) {
    return {
      selectedIndex: propValues.selectedIndex || '',
      selectedExpiry: propValues.selectedExpiry || '',
      defaultQuantity: propValues.defaultQuantity || propValues.lotSize || 50,
      optionChainData: propValues.optionChainData || null,
      // Provide no-op handlers for fallback mode
      handleIndexChange: propValues.onIndexChange || (() => {}),
      handleExpiryChange: propValues.onExpiryChange || (() => {}),
      handleDefaultQuantityChange: () => {},
      handleOptionChainDataChange: propValues.onOptionChainDataChange || (() => {}),
      // Direct setters (will be no-ops in fallback mode)
      setSelectedIndex: () => {},
      setSelectedExpiry: () => {},
      setDefaultQuantity: () => {},
      setOptionChainData: () => {},
    };
  }

  return context;
};

// Export the context for advanced usage if needed
export { TradingContext };